import { Injectable } from "@angular/core";
import { ApplicationInsights, ICustomProperties, ITelemetryItem } from "@microsoft/applicationinsights-web";
import { Environment } from "../../configuration";
import { applyAppRoleName, resolveAppRoleName } from "./application-insights.util";

@Injectable({
    providedIn: "root"
})
export class ApplicationInsightsService {
    private readonly instrumentationKey: string;
    private appInsights: ApplicationInsights;
    public get sessionId(): string {
        const id = this.appInsights.context.getSessionId();
        return typeof id === "string" ? id : "";
    }

    constructor(private environment: Environment) {
        const envKey = this.environment.appInsights?.instrumentationKey;
        this.instrumentationKey = typeof envKey === "string" ? envKey : "";
        this.appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: this.instrumentationKey,
                enableAutoRouteTracking: true
            }
        });

        if (this.instrumentationKey) {
            this.appInsights.loadAppInsights();
            this.setAppRoleName();
        }
    }

    private setAppRoleName(): void {
        const appRoleName = resolveAppRoleName(this.environment.brand?.name);
        this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => applyAppRoleName(envelope, appRoleName));
    }

    public trackException(exception: Error, customProperties?: ICustomProperties): void {
        if (!this.instrumentationKey) return;
        try {
            this.appInsights.trackException({ exception }, customProperties);
        } catch (error) {
            console.error(error);
        }
    }

    public setUserContext(userId: string, organizationId: string): void {
        if (!this.instrumentationKey) return;
        try {
            if (userId && organizationId) {
                this.appInsights.context.user.id = userId;
                this.setAppInsightsCustomFields(organizationId);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private setAppInsightsCustomFields(organizationId: string): void {
        const telemetryInitializer = (envelope: any) => {
            envelope.data.organizationId = organizationId;
            envelope.data.buildNumber = this.environment.buildNumber;
        };
        this.appInsights.addTelemetryInitializer(telemetryInitializer);
    }
}
